import axios from "axios";
import * as Sentry from "@sentry/react";
import {
  getStateLoggedInUserId,
  getToken,
  getTokenUserId,
} from "../redux/store-utils";
import { getTrustedDeviceToken } from "../utils/trustedDevice";

export const AtiraApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    "X-Atria-header": "true",
  },
});

let identityRecoveryInFlight = false;

/**
 * The account the UI is rendering and the token we authenticate with come from
 * two independent places (redux state vs. web storage), and web storage can be
 * changed by other tabs. If they ever disagree, the API would faithfully return
 * the *token's* data while the UI kept displaying the other account - a silent
 * cross-account data leak.
 *
 * So we treat "token identity === state identity" as a hard invariant: on a
 * mismatch we refuse to send the request at all, and reload the tab once so it
 * boots consistently from whatever the current session actually is.
 */
const onIdentityMismatch = (stateUserId: string, tokenUserId: string) => {
  Sentry.captureMessage("Token/state identity mismatch", {
    level: "error",
    tags: { location: "AtiraApi", errorType: "identityMismatch" },
    contexts: { identity: { stateUserId, tokenUserId } },
  });

  if (identityRecoveryInFlight) {
    return;
  }

  identityRecoveryInFlight = true;
  window.location.reload();
};

AtiraApi.interceptors.request.use(
  (config) => {
    const token = getToken();

    if (token) {
      const stateUserId = getStateLoggedInUserId();
      const tokenUserId = getTokenUserId(token);

      if (stateUserId && tokenUserId && stateUserId !== tokenUserId) {
        onIdentityMismatch(stateUserId, tokenUserId);

        return Promise.reject(
          new axios.Cancel(
            `Request blocked: session belongs to ${tokenUserId} but the app is rendering ${stateUserId}.`,
          ),
        );
      }

      config.headers["Authorization"] = `Bearer ${token}`;
    }

    // Let a 2FA login on a "remembered" browser skip the emailed code. Only the
    // login endpoint cares; sending it elsewhere is harmless but pointless.
    if (config.url?.includes("/auth/login")) {
      const trustedDevice = getTrustedDeviceToken();

      if (trustedDevice) {
        config.headers["x-trusted-device"] = trustedDevice;
      }
    }

    return config;
  },
  (error) => Promise.reject(error),
);

let logoutInFlight = false;

AtiraApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    // The server will only send a 401 if the token is invalid/expired and we need to log the user out
    if (error.response?.status === 401 || error.status === 401) {
      if (!logoutInFlight) {
        logoutInFlight = true;

        const store = await import("../redux/store").then((m) => m.default);
        const { userActions } = await import("../redux/user/user.slice");

        await store.store.dispatch(userActions.logoutAndClearPersistence());

        // allow future logouts later (not immediately)
        setTimeout(() => {
          logoutInFlight = false;
        }, 1000);
      }

      return Promise.reject(error);
    }

    if (error.response?.status >= 500 || error.status >= 500) {
      Sentry.captureException(error, {
        tags: { location: "AtiraApi", errorType: "serverError" },
        contexts: {
          response: {
            status: error.response?.status,
            data: error.response?.data,
          },
        },
      });
    }

    return Promise.reject(error);
  },
);
